import React, { useState, useEffect, useCallback } from "react";
import { Drawer, Button, Space, Alert, message, Spin, Typography, Switch, Divider, Progress, Modal } from "antd";
import { MailOutlined, TeamOutlined, UserOutlined } from "@ant-design/icons";
import { scheduleService } from "../../../../api/enhancedApi";
import { emailTemplateApi } from "../../../../api/emailTemplateApi";
import GeneralEmailEditor from "../../../../components/EmailEditor/GeneralEmailEditor";
import AircraftTag from "../../../../components/AircraftTag";
import PublishTemplateSelectionModal from "./PublishTemplateSelectionModal";

const { Text } = Typography;

/**
 * 发布邮件发送抽屉组件
 * 用于在航司确认状态下发送发布邮件，支持拆分发送
 *
 * @param {boolean} visible - 抽屉是否可见
 * @param {function} onClose - 关闭抽屉的回调
 * @param {object} schedule - 选中的计划对象
 * @param {function} onSuccess - 发送成功的回调
 */
const PublishEmailSendDrawer = ({ visible, onClose, schedule, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [scheduleWithFlights, setScheduleWithFlights] = useState(null);
    const [emailTemplate, setEmailTemplate] = useState(null);
    const [isEmailEditorVisible, setIsEmailEditorVisible] = useState(false);
    const [isEmailLoading, setIsEmailLoading] = useState(false);
    const [useSplitSending, setUseSplitSending] = useState(false);
    const [sendingProgress, setSendingProgress] = useState(null);
    const [isTemplateSelectionVisible, setIsTemplateSelectionVisible] = useState(false);
    const [selectedTemplateCode, setSelectedTemplateCode] = useState(null);

    // 加载计划的航班数据
    const loadScheduleData = useCallback(async () => {
        if (!schedule) return;

        try {
            setLoading(true);
            const flightsResponse = await scheduleService.getScheduleFlights(schedule.id, {
                page: 1,
                limit: 1000,
            });

            // 确保flights始终是一个数组
            let flightsData = [];
            if (flightsResponse?.data?.items && Array.isArray(flightsResponse.data.items)) {
                flightsData = flightsResponse.data.items;
            } else if (flightsResponse?.data && Array.isArray(flightsResponse.data)) {
                flightsData = flightsResponse.data;
            } else if (flightsResponse?.items && Array.isArray(flightsResponse.items)) {
                flightsData = flightsResponse.items;
            } else if (Array.isArray(flightsResponse)) {
                flightsData = flightsResponse;
            } else {
                console.warn("无法识别的API响应格式:", flightsResponse);
                flightsData = [];
            }

            setScheduleWithFlights({
                ...schedule,
                flights: flightsData,
            });
        } catch (error) {
            console.error(`加载计划 ${schedule.id} 的航班数据失败:`, error);
            message.error("加载计划数据失败，请重试");
            setScheduleWithFlights({
                ...schedule,
                flights: [],
                loadError: error.message || "加载航班数据失败",
            });
        } finally {
            setLoading(false);
        }
    }, [schedule]);

    // 打开模板选择
    const handleOpenTemplateSelection = useCallback(() => {
        setIsTemplateSelectionVisible(true);
    }, []);

    // 处理模板选择
    const handleTemplateSelect = useCallback(async (templateCode) => {
        try {
            setSelectedTemplateCode(templateCode);
            setIsEmailLoading(true);

            const templateResponse = await emailTemplateApi.getTemplateByCodeWithRecipients(templateCode);

            if (templateResponse.data) {
                setEmailTemplate(templateResponse.data);
                console.log("[PublishEmailSendDrawer] 已加载邮件模板和收件人配置:", templateResponse.data);
                setIsEmailEditorVisible(true);
            } else {
                throw new Error("获取邮件模板失败：服务器返回空数据");
            }
        } catch (error) {
            console.error("获取邮件模板失败:", error);
            // 如果没有发布邮件模板，回退到确认邮件模板
            try {
                const fallbackResponse = await emailTemplateApi.getTemplateByCodeWithRecipients("SCHEDULE_AAE_NEW_CONFIRMATION");
                if (fallbackResponse.data) {
                    setEmailTemplate({
                        ...fallbackResponse.data,
                        subject_template: "航班计划发布通知 - {aircraftText} on {dateRangeText}",
                    });
                    setIsEmailEditorVisible(true);
                    message.warning("使用确认邮件模板作为发布邮件模板");
                } else {
                    throw new Error("无法获取任何可用的邮件模板");
                }
            } catch (fallbackError) {
                message.error(`获取邮件模板失败: ${fallbackError.message || "未知错误"}`);
            }
        } finally {
            setIsEmailLoading(false);
        }
    }, []);

    // 当抽屉打开时加载数据
    useEffect(() => {
        if (visible && schedule) {
            loadScheduleData();
        }
    }, [visible, schedule, loadScheduleData]);

    // 处理邮件发送
    const handleEmailSubmit = async (emailData) => {
        try {
            setIsEmailLoading(true);

            // 提取收件人信息
            const recipients = Array.isArray(emailData.to) ? emailData.to : emailData.to ? [emailData.to] : [];
            const ccRecipients = Array.isArray(emailData.cc) ? emailData.cc : emailData.cc ? [emailData.cc] : [];

            if (recipients.length === 0 && ccRecipients.length === 0) {
                message.error("请至少指定一个收件人");
                return;
            }

            // 统一的邮件数据格式转换
            const convertEmailField = (field) => {
                if (Array.isArray(field)) {
                    return field.length > 0 ? field.join(", ") : null;
                }
                return field || null;
            };

            const emailRequestData = {
                to: convertEmailField(emailData.to) || "",
                cc: convertEmailField(emailData.cc),
                bcc: convertEmailField(emailData.bcc),
                subject: emailData.subject || "",
                rendered_html: emailData.contentHtml || "",
                content_block_context: emailData.contentBlockContext || {
                    template_code: selectedTemplateCode || "SCHEDULE_OUTSTATION_NEW_PUBLICATION",
                    instances: [],
                    business_context_snapshot: {},
                },
                attachments: emailData.attachments || [],
            };

            if (useSplitSending) {
                const { sendScheduleBulkEmail } = await import("../../../../api/scheduleEmailApi");
                const response = await sendScheduleBulkEmail(schedule.id, emailRequestData, true, 1.0);
                setSendingProgress(null);

                if (response.success) {
                    // 异步发送成功，立即返回
                    message.success(`群发邮件创建成功！正在后台发送给 ${response.data?.total_recipients || "多个"} 个收件人，请在活动记录中查看发送进度。`);
                    setIsEmailEditorVisible(false);
                    onSuccess && onSuccess();
                } else {
                    message.error(response.message || "群发邮件创建失败");
                }
            } else {
                // 普通发送模式：使用V2发布邮件API，发送一封传统邮件
                const { sendSchedulePublishEmailV2 } = await import("../../../../api/scheduleEmailApi");
                const response = await sendSchedulePublishEmailV2(schedule.id, emailRequestData);
                if (response.success) {
                    message.success("发布邮件发送成功");
                    setIsEmailEditorVisible(false);
                    onSuccess && onSuccess();
                } else {
                    message.error(response.message || "邮件发送失败");
                }
            }
        } catch (error) {
            console.error("发送邮件失败:", error);
            setSendingProgress(null);

            // 安全地提取错误消息
            let errorMessage = "发送邮件时发生错误";

            if (error.response?.data?.detail) {
                if (Array.isArray(error.response.data.detail)) {
                    const validationErrors = error.response.data.detail
                        .map((err) => {
                            const field = err.loc ? err.loc.join(".") : "未知字段";
                            return `${field}: ${err.msg}`;
                        })
                        .join("; ");
                    errorMessage = `数据验证失败: ${validationErrors}`;
                } else if (typeof error.response.data.detail === "string") {
                    errorMessage = error.response.data.detail;
                }
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            message.error(errorMessage);
        } finally {
            setIsEmailLoading(false);
        }
    };

    // 关闭邮件编辑器
    const handleEmailCancel = () => {
        setIsEmailEditorVisible(false);
        setSendingProgress(null);
    };

    // 生成邮件上下文数据
    const generateEmailContextData = useCallback(() => {
        if (!scheduleWithFlights) return {};

        // 为邮件主题准备日期范围文本
        const dateRange = {
            start_date: scheduleWithFlights.start_date,
            end_date: scheduleWithFlights.end_date,
        };

        const startDate = new Date(dateRange.start_date);
        const endDate = new Date(dateRange.end_date);

        const formatDateForSubject = (date) => {
            const day = date.getDate().toString().padStart(2, "0");
            const month = date.toLocaleString("en-US", { month: "short" }).toUpperCase();
            return `${day}${month}`;
        };

        const dateRangeText = `${formatDateForSubject(startDate)}-${formatDateForSubject(endDate)}`;
        const aircraftText = scheduleWithFlights.aircraft?.registration || "未知飞机";

        // 创建内容块
        const contentBlocks = [];

        // 添加基本信息内容块 - 用于内联文本显示（aircraftText）
        contentBlocks.push({
            id: "schedule_basic_info",
            type: "scheduleFlightList",
            title: "计划基本信息",
            data: scheduleWithFlights.flights || [], // 传递flights数组，但这个内容块主要用于内联文本
            // 为内联文本提供额外的上下文数据
            inlineText: `${aircraftText} on ${dateRangeText}`,
            aircraftText: aircraftText,
            dateRangeText: dateRangeText,
            business_source: {
                entity_type: "Schedule",
                entity_ids: [scheduleWithFlights.id],
            },
            // 为Excel导出添加额外的上下文信息
            schedule_info: scheduleWithFlights,
        });

        // 添加航班列表内容块 - 用于显示详细的航班表格
        contentBlocks.push({
            id: "flight_list",
            type: "scheduleFlightList",
            title: "计划航班列表",
            data: scheduleWithFlights.flights || [], // 传递flights数组
            business_source: {
                entity_type: "ScheduleFlight",
                entity_ids: (scheduleWithFlights.flights || []).filter((flight) => flight.id).map((flight) => flight.id),
            },
            // 为Excel导出添加额外的上下文信息
            schedule_info: scheduleWithFlights,
        });

        return {
            schedule: scheduleWithFlights,
            flights: scheduleWithFlights.flights || [],
            contentBlocks: contentBlocks,
        };
    }, [scheduleWithFlights]);

    // 渲染计划信息
    const renderScheduleInfo = () => {
        if (loading) {
            return (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                    <Spin size="large" />
                    <p style={{ marginTop: 16 }}>正在加载计划数据...</p>
                </div>
            );
        }

        if (!scheduleWithFlights) {
            return <Alert message="未选择计划" type="warning" showIcon />;
        }

        return (
            <div className="schedule-info">
                <Alert
                    message="即将发送发布邮件的计划"
                    description={
                        <div style={{ marginTop: "8px" }}>
                            <div>
                                <Text strong>{scheduleWithFlights.name || "未命名计划"}</Text>
                                {scheduleWithFlights.aircraft && (
                                    <span style={{ marginLeft: "8px" }}>
                                        <AircraftTag registration={scheduleWithFlights.aircraft.registration} />
                                    </span>
                                )}
                            </div>
                            <div style={{ color: "#666", fontSize: "12px", marginTop: "4px" }}>
                                航班数量: {scheduleWithFlights.flights?.length || 0}
                                {scheduleWithFlights.loadError && (
                                    <Text type="danger" style={{ marginLeft: "8px" }}>
                                        (加载错误: {scheduleWithFlights.loadError})
                                    </Text>
                                )}
                            </div>
                        </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            </div>
        );
    };

    return (
        <>
            <Drawer
                title={
                    <span>
                        <MailOutlined style={{ marginRight: 8 }} />
                        发送发布邮件
                    </span>
                }
                placement="right"
                width="600px"
                onClose={onClose}
                open={visible}
                extra={
                    <Space>
                        <Button onClick={onClose}>取消</Button>
                        <Button type="primary" onClick={handleOpenTemplateSelection} disabled={loading || !scheduleWithFlights} loading={isEmailLoading} icon={<MailOutlined />}>
                            编辑邮件内容
                        </Button>
                    </Space>
                }
                destroyOnHidden={true}
            >
                <div className="publish-email-drawer-content">
                    <div className="publish-email-instructions">
                        <Alert
                            message="发送发布邮件"
                            description={
                                <div>
                                    <p>为已确认的航班计划发送发布通知邮件：</p>
                                    <ul>
                                        <li>邮件将包含计划的详细信息和航班列表</li>
                                        <li>发送后计划状态保持不变（仍为"航司确认"）</li>
                                        <li>支持拆分发送，为每个收件人发送单独的邮件</li>
                                    </ul>
                                </div>
                            }
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    </div>

                    {renderScheduleInfo()}

                    <Divider />

                    {/* 拆分发送选项 */}
                    <div className="split-sending-option" style={{ marginBottom: 16 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <div>
                                <Text strong>拆分发送</Text>
                                <div style={{ color: "#666", fontSize: "12px", marginTop: "4px" }}>为每个收件人和抄送人发送单独的邮件</div>
                            </div>
                            <Switch checked={useSplitSending} onChange={setUseSplitSending} checkedChildren={<TeamOutlined />} unCheckedChildren={<UserOutlined />} />
                        </div>

                        {useSplitSending && <Alert message="拆分发送模式" description="每个收件人将收到一封单独的邮件，支持个性化内容。发送间隔为1秒，避免被识别为垃圾邮件。" type="warning" showIcon style={{ marginTop: 12 }} />}
                    </div>
                </div>
            </Drawer>

            {/* 邮件编辑器 */}
            {isEmailEditorVisible && emailTemplate && (
                <GeneralEmailEditor
                    visible={isEmailEditorVisible}
                    onSubmit={handleEmailSubmit}
                    onCancel={handleEmailCancel}
                    title={useSplitSending ? "发送发布邮件 (拆分发送)" : "发送发布邮件"}
                    subjectTemplate={emailTemplate?.subject_template || "航班计划发布通知 - {aircraftText} on {dateRangeText}"}
                    initialSubject={`航班计划发布通知 - ${scheduleWithFlights?.name || "未命名计划"}`}
                    templateContent={emailTemplate?.body_html || ""}
                    templateRecipients={{
                        default_to: emailTemplate?.resolved_to || [],
                        default_cc: emailTemplate?.resolved_cc || [],
                    }}
                    businessContext={generateEmailContextData()}
                    enableV2Mode={true}
                />
            )}

            {/* 发送进度Modal */}
            {sendingProgress && sendingProgress.isVisible && (
                <Modal title="正在发送邮件" open={true} footer={null} closable={false} maskClosable={false} centered>
                    <div style={{ textAlign: "center", padding: "20px 0" }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16 }}>
                            <Text>正在拆分发送邮件...</Text>
                        </div>
                        <div style={{ marginTop: 8 }}>
                            <Progress percent={Math.round((sendingProgress.current / sendingProgress.total) * 100)} status="active" />
                        </div>
                        <div style={{ color: "#666", fontSize: "12px", marginTop: 8 }}>
                            {sendingProgress.current} / {sendingProgress.total} 封邮件
                        </div>
                    </div>
                </Modal>
            )}

            {/* 模板选择模态框 */}
            <PublishTemplateSelectionModal visible={isTemplateSelectionVisible} onClose={() => setIsTemplateSelectionVisible(false)} onSelectTemplate={handleTemplateSelect} />
        </>
    );
};

export default PublishEmailSendDrawer;
